import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { CaretLeft, CaretRight, Baby, SunHorizon, Leaf, Egg, Carrot, Drop, Orange, FilePdf, Warning, X } from '@phosphor-icons/react';
import ShareButton from '@/components/ShareButton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;
const DAYS = ['segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado', 'domingo'];
const DAY_LABELS = { segunda: 'Segunda', terca: 'Terça', quarta: 'Quarta', quinta: 'Quinta', sexta: 'Sexta', sabado: 'Sábado', domingo: 'Domingo' };
const MEAL_LABELS = { pequeno_almoco: 'Pequeno-almoço', meio_manha: 'Meio da manhã', almoco: 'Almoço', meio_tarde: 'Meio da tarde', jantar: 'Jantar', ceia: 'Ceia' };
const GROUP_ICONS = { energia: Leaf, ferro_proteina: Egg, horticolas: Carrot, gordura: Drop, micronutrientes: Orange };

function AllergenIcon({ food, allergenMap, onClickAllergen }) {
  const info = allergenMap[(food || '').toLowerCase()];
  if (!info) return null;
  return (
    <button onClick={(e) => { e.stopPropagation(); onClickAllergen(info); }}
      className="ml-1 inline-flex items-center" title={`Alergénio: ${info.category}`} data-testid={`allergen-icon-${food}`}>
      <Warning size={12} weight="fill" className="text-amber-500" />
    </button>
  );
}

function getTodayKey() {
  const d = new Date().getDay();
  return DAYS[d === 0 ? 6 : d - 1];
}

const localYmd = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const ptDate = (ymd) => ymd.split('-').reverse().join('/');

// Semana e dia de hoje pela data de início de cada semana publicada (não pela segunda-feira).
export function resolveToday(weeks, todayYmd, todayKey) {
  const dated = weeks.filter(w => (w.days_ordered || []).some(d => d.date));
  if (dated.length === 0) {
    const w = [...weeks].reverse().find(x => (x.days?.[todayKey] || []).length > 0);
    return w ? { week: w, meals: w.days[todayKey] } : { message: 'Sem refeições programadas para hoje.' };
  }
  const allDates = dated.flatMap(w => w.days_ordered.map(d => d.date)).filter(Boolean).sort();
  for (const w of dated) {
    const day = w.days_ordered.find(d => d.date === todayYmd);
    if (day) return { week: w, meals: day.items || [] };
  }
  if (todayYmd < allDates[0]) return { message: `A ementa publicada começa a ${ptDate(allDates[0])}.` };
  if (todayYmd > allDates[allDates.length - 1]) return { message: `A última ementa publicada terminou a ${ptDate(allDates[allDates.length - 1])}. Aguarda a próxima ementa da tua nutricionista.` };
  return { message: 'Não há ementa publicada para hoje.' };
}

function TodayView({ weeks, allergenMap, onClickAllergen }) {
  const todayKey = getTodayKey();
  const todayLabel = DAY_LABELS[todayKey];
  const { week: currentWeek, meals, message } = resolveToday(weeks, localYmd(), todayKey);

  if (!currentWeek || meals.length === 0) {
    return (
      <div className="text-center py-12" data-testid="baby-today-empty">
        <SunHorizon size={48} className="mx-auto text-brand-placeholder mb-4" weight="duotone" />
        <p className="text-brand-muted text-sm" data-testid="baby-today-message">{message || `Sem refeições programadas para hoje (${todayLabel}).`}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="baby-today-view">
      <div className="text-center mb-4">
        <p className="text-xs text-brand-muted uppercase tracking-wider">{todayLabel}</p>
        <p className="text-sm text-brand-green font-medium mt-0.5">{currentWeek.focus || currentWeek.label}</p>
      </div>
      {meals.map((meal, i) => (
        <div key={i} className="bg-white border border-brand-border rounded-2xl p-4" data-testid={`today-meal-${i}`}>
          <h3 className="font-heading font-medium text-sm text-brand-text mb-2">{MEAL_LABELS[meal.type] || meal.type}</h3>
          <div className="space-y-1.5">
            {(meal.items || []).map((item, j) => {
              const Icon = GROUP_ICONS[item.group] || Leaf;
              return (
                <div key={j} className="flex items-center gap-2">
                  <Icon size={14} className="text-brand-green flex-shrink-0" weight="duotone" />
                  <span className="text-sm text-brand-text">{item.food}</span>
                  <AllergenIcon food={item.food} allergenMap={allergenMap} onClickAllergen={onClickAllergen} />
                </div>
              );
            })}
          </div>
          {meal.tip && <p className="text-xs text-brand-green bg-brand-green/5 rounded-xl px-3 py-2 mt-3">{meal.tip}</p>}
          {meal.note && <p className="text-xs text-brand-muted mt-2 italic">{meal.note}</p>}
          {(meal.info_lines || []).length > 0 && (
            <div className="mt-2 space-y-0.5">
              {meal.info_lines.map((line, li) => (
                <p key={li} className="text-[10px] text-brand-green/70 italic">. {line}</p>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function formatNoteLines(text) {
  return text.trim().split(/\n\s*\n/).map((block, bi) => (
    <p key={bi} className="mb-2 last:mb-0">
      {block.split('\n').map((raw, li) => {
        const line = raw.trim();
        const isBullet = line.startsWith('- ');
        const content = isBullet ? line.slice(2) : line;
        const parts = content.split(/\*\*(.+?)\*\*/g);
        return (
          <span key={li} className="block">
            {isBullet && '•  '}
            {parts.map((p, pi) => (pi % 2 === 1 ? <b key={pi}>{p}</b> : p))}
          </span>
        );
      })}
    </p>
  ));
}

function WeekNote({ week }) {
  const note = (week.week_note || '').trim();
  if (!note || week.week_note_visibility !== 'mae') return null;
  return (
    <div className="bg-brand-green/5 border border-brand-green/20 rounded-2xl p-4 mb-4" data-testid="baby-week-note">
      <p className="text-xs font-heading font-medium text-brand-green mb-1.5">Nota da semana</p>
      <div className="text-sm text-brand-text">{formatNoteLines(note)}</div>
    </div>
  );
}

function ProgramView({ weeks, allergenMap, onClickAllergen }) {
  const [weekIdx, setWeekIdx] = useState(() => {
    const cur = resolveToday(weeks, localYmd(), getTodayKey()).week;
    const i = cur ? weeks.indexOf(cur) : -1;
    return i >= 0 ? i : (weeks.length > 0 ? weeks.length - 1 : 0);
  });
  const week = weeks[weekIdx];

  const downloadPdf = async () => {
    if (!week) return;
    try {
      const r = await axios.get(`${API}/api/baby-menus/${week.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `ementa_bebe_${(week.label || 'S1').replace(/ /g, '_')}.pdf`; a.click();
    } catch {}
  };

  if (!week) {
    return <div className="text-center py-12 text-brand-muted text-sm">Sem programa disponível.</div>;
  }

  const days = week.days || {};
  const dayOrder = Array.isArray(week.days_ordered) && week.days_ordered.length === 7
    ? week.days_ordered.map(d => ({ day: d.day, label: d.date ? `${d.weekday_label} ${d.date_label}` : d.weekday_label }))
    : DAYS.map((d) => ({ day: d, label: DAY_LABELS[d] }));

  return (
    <div data-testid="baby-program-view">
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => setWeekIdx(Math.max(0, weekIdx - 1))} disabled={weekIdx === 0} className="p-2 rounded-full hover:bg-brand-bg disabled:opacity-30"><CaretLeft size={20} className="text-brand-green" /></button>
        <div className="text-center">
          <p className="font-heading font-medium text-brand-text">{week.label || 'Ementa semanal'}</p>
          <p className="text-xs text-brand-muted">{week.focus}</p>
        </div>
        <button onClick={() => setWeekIdx(Math.min(weeks.length - 1, weekIdx + 1))} disabled={weekIdx >= weeks.length - 1} className="p-2 rounded-full hover:bg-brand-bg disabled:opacity-30"><CaretRight size={20} className="text-brand-green" /></button>
      </div>

      <div className="flex justify-center mb-3">
        <button onClick={downloadPdf} className="text-xs text-brand-green border border-brand-border rounded-full px-4 py-1.5 flex items-center gap-1.5 hover:bg-brand-green/5" data-testid="baby-pdf-btn">
          <FilePdf size={14} /> Exportar PDF
        </button>
      </div>

      <WeekNote week={week} />

      <div className="overflow-x-auto -mx-4 px-4 pb-2">
        <div className="inline-flex gap-2 min-w-max">
          {dayOrder.map(({ day, label }) => {
            const meals = days[day] || [];
            return (
              <div key={day} className="w-[140px] bg-white border border-brand-border rounded-2xl overflow-hidden flex-shrink-0">
                <div className="bg-brand-bg/50 py-1.5 px-2 text-center border-b border-brand-border">
                  <span className="text-[10px] font-semibold text-brand-muted uppercase">{label}</span>
                </div>
                <div className="p-2 space-y-1.5 min-h-[80px]">
                  {meals.length === 0 ? (
                    <p className="text-[9px] text-brand-placeholder text-center py-4"> </p>
                  ) : meals.map((meal, mi) => (
                    <div key={mi} className="bg-brand-bg/50 rounded-lg p-1.5">
                      <p className="text-[9px] font-semibold text-brand-green uppercase">{MEAL_LABELS[meal.type] || meal.type}</p>
                      {(meal.items || []).map((item, ii) => (
                        <div key={ii} className="flex items-center gap-0.5">
                          <p className="text-[10px] text-brand-text">{item.food}</p>
                          <AllergenIcon food={item.food} allergenMap={allergenMap} onClickAllergen={onClickAllergen} />
                        </div>
                      ))}
                      {meal.tip && <p className="text-[9px] text-brand-muted mt-0.5 italic">{meal.tip}</p>}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function BabyMenuClientPage() {
  const [weeks, setWeeks] = useState([]);
  const [allergenMap, setAllergenMap] = useState({});
  const [view, setView] = useState('hoje');
  const [loading, setLoading] = useState(true);
  const [allergenPopup, setAllergenPopup] = useState(null);
  const [protocols, setProtocols] = useState({ default_text: '', by_category: {} });

  useEffect(() => {
    (async () => {
      try {
        const [wRes, fRes, pRes] = await Promise.all([
          axios.get(`${API}/api/my-baby-menus`),
          axios.get(`${API}/api/baby-foods`),
          axios.get(`${API}/api/baby-allergen-intro-protocols`),
        ]);
        setWeeks(wRes.data);
        setProtocols(pRes.data);
        const map = {};
        (fRes.data || []).forEach(f => {
          const cats = f.allergen_categories?.length ? f.allergen_categories : (f.allergen_category ? [f.allergen_category] : []);
          if (f.is_allergen && cats.length) {
            map[f.name.toLowerCase()] = { name: f.name, category: cats.join(', '), categories: cats };
          }
        });
        setAllergenMap(map);
      } catch {} finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="text-center py-12 text-brand-muted">A carregar...</div>;

  return (
    <div className="fade-in" data-testid="baby-menu-client-page">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading font-medium text-xl text-brand-text">Ementa do Bebé</h2>
        {weeks.length > 0 && <ShareButton resources={{ baby_menu: (resolveToday(weeks, localYmd(), getTodayKey()).week || weeks[weeks.length - 1])?.id }} label="Ementa do bebé" />}
      </div>

      <div className="flex gap-2 mb-5">
        {[{ k: 'hoje', l: 'Hoje' }, { k: 'programa', l: 'Programa' }].map(v => (
          <button key={v.k} onClick={() => setView(v.k)} className={`flex-1 py-2.5 rounded-xl text-sm font-medium ${view === v.k ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`} data-testid={`baby-view-${v.k}`}>
            {v.l}
          </button>
        ))}
      </div>

      {weeks.length === 0 ? (
        <div className="text-center py-16">
          <Baby size={48} className="mx-auto text-brand-placeholder mb-4" weight="duotone" />
          <p className="text-brand-muted text-sm">A tua nutricionista ainda não adicionou a ementa.</p>
        </div>
      ) : view === 'hoje' ? (
        <TodayView weeks={weeks} allergenMap={allergenMap} onClickAllergen={setAllergenPopup} />
      ) : (
        <ProgramView weeks={weeks} allergenMap={allergenMap} onClickAllergen={setAllergenPopup} />
      )}

      {/* Allergen intro popup */}
      <Dialog open={!!allergenPopup} onOpenChange={v => { if (!v) setAllergenPopup(null); }}>
        <DialogContent className="rounded-3xl border-brand-border max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-brand-text flex items-center gap-2">
              <Warning size={18} className="text-amber-500" weight="fill" />
              Alergénio: {allergenPopup?.category}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-brand-text font-medium">{allergenPopup?.name}</p>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
              <p className="text-xs font-semibold text-amber-700 mb-2">Como introduzir</p>
              <p className="text-xs text-amber-800 leading-relaxed">{(allergenPopup?.categories || []).length > 1 ? (allergenPopup.categories).map(c => `${c}: ${protocols.by_category?.[c] || protocols.default_text}`).join('\n\n') : (protocols.by_category?.[allergenPopup?.category] || protocols.default_text)}</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
